import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FiChevronRight, FiMoreVertical } from "react-icons/fi";
import OutsideClickHandler from "react-outside-click-handler/build/OutsideClickHandler";

import { can } from "src/Common/function";
import CustomerAddModal from "./CustomerAddModal";
import LocationAddModal from "./components/LocationAddModal";
import AddCustomerOrLocationModal from "./components/AddCustomerOrLocationModal";
import RestoreDialog from "./components/RestoreDialog";
import { StatusBadge, BranchChips } from "./components/components";
import AccountService from "src/Services/API/account.service";
import CustomerService from "src/Services/API/customer.service";
import BranchService from "src/Services/API/branch.service";
import UserService from "src/Services/API/user.service";
import { formatDate } from "src/Common/common";
import Highlight from "src/Components/Common/Highlight";

const PAGE_SIZE = 10;

// Fields a location is searched on (must mirror the backend keyword filter).
const locationMatchesKeyword = (location, term) => {
  if (!term) return false;
  const needle = term.toLowerCase();
  return [location.companyName, location.contact, location.email, location.phone, location.address].some(
    (field) => field && String(field).toLowerCase().includes(needle)
  );
};

function Customer() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = (searchParams.get("keyword") || "").trim();
  // Customer app users filter: "has", "none" or all (no param). In the URL so
  // it survives a visit to a customer and back, like the page.
  const customerUsersFilter = ["has", "none"].includes(searchParams.get("customerUsers"))
    ? searchParams.get("customerUsers")
    : "";
  const setCustomerUsersFilter = (value) =>
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (value) params.set("customerUsers", value);
      else params.delete("customerUsers");
      params.delete("page");
      return params;
    });

  // Keep the page in the URL so it survives navigating away (e.g. to a customer
  // detail page) and back, instead of resetting to 1 on remount.
  const page = Math.max(parseInt(searchParams.get("page"), 10) || 1, 1);
  // `replace` is used only for automatic resets (e.g. keyword change). User page
  // clicks push a new history entry so the page is restored on Back navigation.
  const setPage = (next, { replace = false } = {}) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        const value = typeof next === "function" ? next(page) : next;
        if (value <= 1) params.delete("page");
        else params.set("page", String(value));
        return params;
      },
      { replace }
    );

  const [activeTab, setActiveTab] = useState("manage"); // manage | archived
  const [accounts, setAccounts] = useState([]);
  const [archivedAccounts, setArchivedAccounts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [expandedAccountIds, setExpandedAccountIds] = useState([]);

  const [isCustomerModalShown, setIsCustomerModalShown] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);

  const [isAddChoiceShown, setIsAddChoiceShown] = useState(false); // "Add Customer Or Location" choice modal
  const [isAddLocationShown, setIsAddLocationShown] = useState(false);
  const [addLocationAccountId, setAddLocationAccountId] = useState(null); // customer to preselect in Add Location
  const [allAccounts, setAllAccounts] = useState([]); // every customer, for the Add Location picker

  const [editingLocation, setEditingLocation] = useState(null); // location being edited
  const [archiveLocationTarget, setArchiveLocationTarget] = useState(null); // location being archived

  const [restoreTarget, setRestoreTarget] = useState(null); // account being restored
  const [archiveTarget, setArchiveTarget] = useState(null); // account being archived

  // IAM permissions. Accounts are the top-level "customer" (customer:*);
  // their child sites are locations (customer:*Location).
  const canEditCustomer = can("customer:update");
  const canArchiveCustomer = can("customer:archive");
  const canRestoreCustomer = can("customer:restore");
  const canEditLocation = can("customer:editLocation");
  const canArchiveLocation = can("customer:archiveLocation");
  // Show the "Add Customer Or Location" entry if the user can do either.
  const canAddCustomerLocation = can("customer:create") || can("customer:addLocation");
  const totalPages = Math.max(Math.ceil(totalCount / PAGE_SIZE), 1);

  useEffect(() => {
    BranchService.getAll().then((response) => setBranches(response.data || []));
  }, []);

  // Reset to the first page only when the keyword actually changes — not on the
  // initial mount, so a restored `?page=N` (e.g. returning from a detail page)
  // is preserved.
  const prevKeywordRef = useRef(keyword);
  useEffect(() => {
    if (prevKeywordRef.current !== keyword) {
      prevKeywordRef.current = keyword;
      setPage(1, { replace: true });
    }
  }, [keyword]);

  useEffect(() => {
    if (activeTab === "manage") {
      loadManage();
    } else {
      loadArchived();
    }
  }, [activeTab, page, keyword, customerUsersFilter]);

  const loadManage = async () => {
    try {
      const accountsRes = await AccountService.getAll({
        page,
        limit: PAGE_SIZE,
        keyword,
        ...(customerUsersFilter && { customerUsers: customerUsersFilter }),
      });
      const payload = accountsRes.data;
      const loaded = payload?.data || [];
      setAccounts(loaded);
      setTotalCount(payload?.totalCount || 0);
      // Auto-expand customers whose match came from a location, so the matching
      // location is visible without the user having to expand manually.
      if (keyword) {
        setExpandedAccountIds(
          loaded
            .filter((account) => (account.customers || []).some((loc) => locationMatchesKeyword(loc, keyword)))
            .map((account) => account.id)
        );
      }
    } catch (error) {
      toast(error?.response?.data?.message || "Failed to load customers");
    }
  };

  const loadArchived = async () => {
    try {
      const response = await AccountService.getArchived();
      setArchivedAccounts(response.data || []);
    } catch (error) {
      toast(error?.response?.data?.message || "Failed to load archived customers");
    }
  };

  const reload = () => (activeTab === "manage" ? loadManage() : loadArchived());

  // ---- create / edit customer ----
  const createCustomer = async (data) => {
    try {
      const response = await AccountService.createWithLocations(data);
      if (response.status === 201 || response.status === 200) {
        toast("Customer created successfully");
        await loadManage();
        setIsCustomerModalShown(false);
        return;
      }
      toast("Something went wrong");
    } catch (error) {
      toast(error?.response?.data?.message || "Failed to create customer");
    }
  };

  const updateCustomer = async (data) => {
    try {
      const response = await AccountService.updateAccount({ id: data.id, name: data.name });
      if (response.status === 201 || response.status === 200) {
        toast("Customer updated successfully");
        await reload();
        setEditingAccount(null);
        setIsCustomerModalShown(false);
        return;
      }
      toast("Something went wrong");
    } catch (error) {
      toast(error?.response?.data?.message || "Failed to update customer");
    }
  };

  // ---- archive / restore customer ----
  const confirmArchive = async () => {
    if (!archiveTarget) return;
    try {
      await AccountService.archiveAccount(archiveTarget.id);
      toast("Customer archived");
      setArchiveTarget(null);
      await loadManage();
    } catch (error) {
      toast(error?.response?.data?.message || "Failed to archive customer");
    }
  };

  const confirmRestore = async () => {
    if (!restoreTarget) return;
    try {
      await AccountService.restoreAccount(restoreTarget.id);
      toast("Customer restored");
      setRestoreTarget(null);
      await loadArchived();
    } catch (error) {
      toast(error?.response?.data?.message || "Failed to restore customer");
    }
  };

  // ---- edit / archive location ----
  const updateLocation = async (location) => {
    try {
      await CustomerService.updateCustomer(location);
      toast("Location updated successfully");
      setEditingLocation(null);
      await reload();
    } catch (error) {
      toast(error?.response?.data?.message || "Failed to update location");
    }
  };

  const confirmArchiveLocation = async () => {
    if (!archiveLocationTarget) return;
    try {
      await CustomerService.archiveCustomer(archiveLocationTarget.id);
      toast("Location archived");
      setArchiveLocationTarget(null);
      await reload();
    } catch (error) {
      toast(error?.response?.data?.message || "Failed to archive location");
    }
  };

  // Load every (non-archived) customer so name-similarity checks and the Add
  // Location picker compare against all customers, not just the current page.
  const loadAllAccounts = async () => {
    try {
      // No pagination → backend returns every (non-archived) customer.
      const response = await AccountService.getAll();
      setAllAccounts(response.data || []);
    } catch (error) {
      toast(error?.response?.data?.message || "Failed to load customers");
    }
  };

  // ---- add location (from list toolbar) ----
  const openAddLocation = (accountId = null) => {
    setAddLocationAccountId(accountId);
    setIsAddLocationShown(true);
    loadAllAccounts();
  };

  // ---- create / edit customer ----
  const openCustomerModal = (accountToEdit = null) => {
    setEditingAccount(accountToEdit);
    setIsCustomerModalShown(true);
    loadAllAccounts();
  };

  const addLocation = async (data) => {
    try {
      const currentUser = UserService.getCurrentUser();
      for (const location of data.locations) {
        await CustomerService.saveCustomer({
          companyName: location.companyName,
          contact: location.contact,
          email: location.email,
          phone: location.phone,
          address: location.address,
          branch: location.branch,
          createdById: currentUser?.id,
          accountId: data.accountId,
        });
      }
      toast("Location(s) saved successfully");
      setIsAddLocationShown(false);
      await reload();
    } catch (error) {
      toast(error?.response?.data?.message || "Failed to save location");
    }
  };

  const toggleExpanded = (id) =>
    setExpandedAccountIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const expandAll = () => setExpandedAccountIds(accounts.map((a) => a.id));
  const collapseAll = () => setExpandedAccountIds([]);

  const branchSummary = (locations) => locations.map((c) => c.branch?.name).filter(Boolean);

  const goToCustomer = (account) => navigate(`/customer/${account.id}`);
  const goToLocation = (location) => navigate(`/location/${location.id}`);

  return (
    <div className="w-full h-full bg-white">
      {isCustomerModalShown && (
        <CustomerAddModal
          account={editingAccount}
          accounts={[...allAccounts, ...archivedAccounts]}
          branches={branches}
          onAddClicked={createCustomer}
          onUpdateClicked={updateCustomer}
          onCreateLocationClicked={(accountId) => {
            setEditingAccount(null);
            setIsCustomerModalShown(false);
            openAddLocation(accountId);
          }}
          onCancelClicked={() => {
            setEditingAccount(null);
            setIsCustomerModalShown(false);
          }}
        />
      )}
      {restoreTarget && (
        <RestoreDialog
          title="Restore Customer"
          description="Restore this customer and all associated locations?"
          details={[
            { label: "Customer", value: restoreTarget.name },
            {
              label: "Locations",
              value: (restoreTarget.customers || []).length,
            },
          ]}
          confirmLabel="Restore Customer"
          onConfirm={confirmRestore}
          onCancel={() => setRestoreTarget(null)}
        />
      )}
      {archiveTarget && (
        <RestoreDialog
          variant="archive"
          title="Are you sure you want to archive this customer?"
          description={[
            <>
              Archiving a customer will also archive{" "}
              <span className="font-OpenSansSemiBold">ALL locations</span> associated with this customer. Historical
              records will remain available.
            </>,
          ]}
          details={[
            { label: "Customer", value: archiveTarget.name },
            {
              label: "Locations",
              value: (archiveTarget.customers || []).filter((c) => !c.isArchived).length,
            },
          ]}
          confirmLabel="Archive Customer"
          onConfirm={confirmArchive}
          onCancel={() => setArchiveTarget(null)}
        />
      )}
      {isAddChoiceShown && (
        <AddCustomerOrLocationModal
          onSelectCustomer={() => {
            setIsAddChoiceShown(false);
            openCustomerModal();
          }}
          onSelectLocation={() => {
            setIsAddChoiceShown(false);
            openAddLocation();
          }}
          onCancel={() => setIsAddChoiceShown(false)}
        />
      )}
      {isAddLocationShown && (
        <CustomerAddModal
          mode="add-location"
          accounts={allAccounts}
          branches={branches}
          preselectedAccountId={addLocationAccountId}
          onAddLocationClicked={addLocation}
          onCancelClicked={() => setIsAddLocationShown(false)}
        />
      )}
      {editingLocation && (
        <LocationAddModal
          branches={branches}
          customers={[editingLocation]}
          customer={editingLocation}
          onUpateClicked={updateLocation}
          onCancelClicked={() => setEditingLocation(null)}
        />
      )}
      {archiveLocationTarget && (
        <RestoreDialog
          variant="archive"
          title="Are you sure you want to archive this location?"
          description={[
            "Archived locations will no longer appear when creating new records.",
            "Historical records will remain unchanged.",
          ]}
          confirmLabel="Archive Location"
          onConfirm={confirmArchiveLocation}
          onCancel={() => setArchiveLocationTarget(null)}
        />
      )}

      {/* Tabs */}
      <div className="flex items-center gap-6 px-6 border-b border-borderColor">
        <TabButton label="Manage" active={activeTab === "manage"} onClick={() => setActiveTab("manage")} />
        <TabButton label="Archived" active={activeTab === "archived"} onClick={() => setActiveTab("archived")} />
      </div>

      {activeTab === "manage" ? (
        <div className="px-6 py-4">
          {/* Toolbar */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <button
                onClick={expandAll}
                className="text-[14px] font-OpenSansSemiBold leading-[20px] tracking-[0.14px] capitalize text-[#7C829A] hover:text-titleColor"
              >
                Expand All
              </button>
              <div className="h-4 w-px bg-borderColor" />
              <button
                onClick={collapseAll}
                className="text-[14px] font-OpenSansSemiBold leading-[20px] tracking-[0.14px] capitalize text-[#7C829A] hover:text-titleColor"
              >
                Collapse All
              </button>
              <div className="h-4 w-px bg-borderColor" />
              <CustomerUsersFilter value={customerUsersFilter} onChange={setCustomerUsersFilter} />
            </div>
            {canAddCustomerLocation && (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsAddChoiceShown(true)}
                  className="h-[32px] px-4 rounded-[8px] bg-[#0065FD] flex items-center gap-1.5 text-[14px] font-OpenSansSemiBold leading-[20px] tracking-[0.14px] capitalize text-white"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 11 11" fill="none">
                    <path
                      d="M9.83333 4.66667C10.1095 4.66667 10.3333 4.89052 10.3333 5.16667C10.3333 5.44281 10.1095 5.66667 9.83333 5.66667H0.5C0.223858 5.66667 0 5.44281 0 5.16667C0 4.89052 0.223858 4.66667 0.5 4.66667H9.83333Z"
                      fill="white"
                    />
                    <path
                      d="M4.66667 9.83333V0.5C4.66667 0.223858 4.89052 0 5.16667 0C5.44281 0 5.66667 0.223858 5.66667 0.5V9.83333C5.66667 10.1095 5.44281 10.3333 5.16667 10.3333C4.89052 10.3333 4.66667 10.1095 4.66667 9.83333Z"
                      fill="white"
                    />
                  </svg>{" "}
                  Add Customer Or Location
                </button>
              </div>
            )}
          </div>

          {/* Table */}
          <div className="border border-[#E5E8F3] rounded-[12px] bg-white overflow-hidden overflow-x-auto shadow-[0_1px_3px_0_rgba(0,0,0,0.00),0_1px_2px_0_rgba(0,0,0,0.06)]">
            <table className="w-full min-w-[1310px] table-fixed">
              <colgroup>
                <col className="w-[48px]" />
                <col className="w-auto" />
                <col className="w-[160px]" />
                <col className="w-[220px]" />
                <col className="w-[250px]" />
                <col className="w-[200px]" />
                <col className="w-[160px]" />
                <col className="w-[48px]" />
              </colgroup>
              <thead>
                <tr className="border-b border-[#E5E8F3]" style={{ background: "#F8F9FE" }}>
                  <Th />
                  <Th>Customer</Th>
                  <Th>Locations</Th>
                  <Th>Branches</Th>
                  <Th>Customer Users</Th>
                  <Th>Added By</Th>
                  <Th>Date Added</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {accounts.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-6 py-8 text-center text-[14px] text-subTitleColor font-OpenSansRegular"
                    >
                      {keyword
                        ? "No customers match your search."
                        : customerUsersFilter
                          ? "No customers match this filter."
                          : "No customers added yet."}
                    </td>
                  </tr>
                ) : (
                  accounts.map((account) => {
                    const isExpanded = expandedAccountIds.includes(account.id);
                    const locations = account.customers || [];

                    return (
                      <Fragment key={account.id}>
                        <tr className="h-[54px] border-b border-[#E5E8F3] hover:bg-[#F8F9FE] ">
                          <td className="pl-6">
                            <button
                              onClick={() => toggleExpanded(account.id)}
                              className="text-subTitleColor h-full flex items-center"
                            >
                              <FiChevronRight
                                size={18}
                                className={`transition-transform duration-200 ease-out ${
                                  isExpanded ? "rotate-90" : "rotate-0"
                                }`}
                              />
                            </button>
                          </td>
                          <td className="px-6">
                            <button
                              onClick={() => goToCustomer(account)}
                              className="text-[14px] font-OpenSansSemiBold leading-[20px] text-[#0058E5] hover:underline text-left"
                            >
                              <Highlight text={account.name} keyword={keyword} />
                            </button>
                          </td>
                          <td className="px-6  text-[14px] font-OpenSansRegular leading-[20px] text-[#1A2332]">
                            {locations.length} Location{locations.length === 1 ? "" : "s"}
                          </td>
                          <td className="px-6">
                            <BranchChips branches={branchSummary(locations)} />
                          </td>
                          <td className="px-6">
                            <CustomerUsersCell summary={account.customerUserSummary} />
                          </td>
                          <td className="px-6 text-[14px] font-OpenSansRegular leading-[20px] text-[#1A2332]">
                            {account.createdBy?.name || "—"}
                          </td>
                          <td className="px-6 text-[14px] font-OpenSansRegular leading-[20px] text-[#1A2332]">
                            {account.createdAt ? formatDate(account.createdAt, "MMM D, YYYY") : "—"}
                          </td>
                          <td className="px-6">
                            {(canEditCustomer || canArchiveCustomer) && (
                              <RowMenu
                                canEdit={canEditCustomer}
                                canArchive={canArchiveCustomer}
                                onEdit={() => openCustomerModal(account)}
                                onArchive={() => setArchiveTarget(account)}
                              />
                            )}
                          </td>
                        </tr>

                        <tr className={isExpanded ? "bg-[#FCFCFD] border-b border-borderColor" : ""}>
                          <td colSpan={8} className="p-0">
                            <div
                              className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
                                isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                              }`}
                            >
                              <div
                                className={`overflow-hidden transition-opacity duration-300 ${
                                  isExpanded ? "opacity-100" : "opacity-0"
                                }`}
                              >
                                <LocationSubTable
                                  locations={locations}
                                  keyword={keyword}
                                  onRowClick={goToLocation}
                                  canEdit={canEditLocation}
                                  canArchive={canArchiveLocation}
                                  onEdit={(loc) => setEditingLocation(loc)}
                                  onArchive={(loc) => setArchiveLocationTarget(loc)}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      </Fragment>
                    );
                  })
                )}
              </tbody>
            </table>

            {/* Pagination */}
            <Pagination
              page={page}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={PAGE_SIZE}
              currentCount={accounts.length}
              onChange={setPage}
            />
          </div>
        </div>
      ) : (
        <ArchivedTab
          accounts={archivedAccounts}
          canRestore={canRestoreCustomer}
          onRestore={(account) => setRestoreTarget(account)}
        />
      )}
    </div>
  );
}

function TabButton({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        color: active ? "#0058E5" : "#7C829A",
        fontFamily: "OpenSansRegular",
        fontSize: "14px",
        fontWeight: 600,
        lineHeight: "20px",
        letterSpacing: "0.14px",
        textTransform: "capitalize",
      }}
      className="relative py-4 text-center"
    >
      {label}
      {active && <span className="absolute left-0 -bottom-px w-full h-[2px] bg-blueColor" />}
    </button>
  );
}

// All / Has / None pills for the customer app users filter.
function CustomerUsersFilter({ value, onChange }) {
  const options = [
    { value: "", label: "All" },
    { value: "has", label: "Has Customer Users" },
    { value: "none", label: "No Customer Users" },
  ];
  return (
    <div className="flex items-center gap-1">
      {options.map((option) => (
        <button
          key={option.value || "all"}
          onClick={() => onChange(option.value)}
          className={`h-[28px] px-3 rounded-full text-[13px] leading-[18px] ${
            value === option.value
              ? "bg-lightBlue text-blueColor font-OpenSansSemiBold"
              : "text-[#7C829A] font-OpenSansRegular hover:bg-backgroundColor"
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

// Who uses the customer apps: each active user by name (Administrators first,
// tagged Admin; email on hover), then unaccepted invites. Shows a few and
// counts the rest. "No admin" in amber flags users with no Administrator.
const CUSTOMER_USERS_SHOWN = 3;

function CustomerUsersCell({ summary }) {
  const people = summary?.people || [];
  const invited = summary?.invited || [];
  if (!people.length && !invited.length) {
    return <span className="text-[14px] font-OpenSansRegular leading-[20px] text-subTitleColor">—</span>;
  }
  const shown = people.slice(0, CUSTOMER_USERS_SHOWN);
  const hidden = people.slice(CUSTOMER_USERS_SHOWN);
  const hasAdmin = people.some((person) => person.admin);
  return (
    <div className="flex flex-col gap-0.5 py-2">
      {shown.map((person) => (
        <span
          key={person.id}
          title={person.email}
          className="flex items-start gap-1.5 text-[13px] font-OpenSansRegular leading-[18px] text-[#1A2332]"
        >
          <span className="min-w-0 break-words">{person.name || person.email}</span>
          {person.admin && (
            <span className="shrink-0 px-1.5 rounded-[4px] text-[11px] font-OpenSansSemiBold leading-[16px] bg-lightBlue text-blueColor">
              Admin
            </span>
          )}
        </span>
      ))}
      {hidden.length > 0 && (
        <span
          title={hidden.map((person) => `${person.name || ""} <${person.email}>`).join("\n")}
          className="text-[12px] font-OpenSansRegular leading-[18px] text-subTitleColor"
        >
          +{hidden.length} more
        </span>
      )}
      {people.length > 0 && !hasAdmin && (
        <span className="text-[12px] font-OpenSansRegular leading-[18px] text-[#B54708]">No admin</span>
      )}
      {invited.length > 0 && (
        <span
          title={invited.map((invite) => (invite.name ? `${invite.name} <${invite.email}>` : invite.email)).join("\n")}
          className="text-[12px] font-OpenSansRegular leading-[18px] text-subTitleColor"
        >
          {invited.length} invited
        </span>
      )}
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`h-[44px] px-6 py-3 text-left text-[12px] font-OpenSansSemiBold leading-[18px] text-[#1A2332] ${className}`}
    >
      {children}
    </th>
  );
}

function LocationSubTable({ locations, keyword = "", onRowClick, canEdit = false, canArchive = false, onEdit, onArchive }) {
  if (locations.length === 0) {
    return (
      <div className="px-10 py-4 text-[13px] text-subTitleColor font-OpenSansRegular">
        No locations have been added to this customer yet.
      </div>
    );
  }

  return (
    <table className="w-full table-fixed">
      <colgroup>
        {/* aligns with main table: chevron(48) + Customer(auto) */}
        <col className="w-auto" />
        {/* Locations(160) */}
        <col className="w-[160px]" />
        {/* Branches(220) */}
        <col className="w-[220px]" />
        {/* Customer Users(250) */}
        <col className="w-[250px]" />
        {/* Added By(200) */}
        <col className="w-[200px]" />
        {/* Date Added(160) */}
        <col className="w-[160px]" />
        {/* menu(48) */}
        <col className="w-[48px]" />
      </colgroup>
      <thead>
        <tr className="border-b border-borderColor">
          <th className="pl-[72px] pr-6 py-2.5 text-left text-[12px] font-OpenSansSemiBold text-subTitleColor">
            Location Name
          </th>
          <th className="px-6 py-2.5 text-left text-[12px] font-OpenSansSemiBold text-subTitleColor">Branch</th>
          <th className="px-6 py-2.5 text-left text-[12px] font-OpenSansSemiBold text-subTitleColor">Contact Person</th>
          <th className="px-6 py-2.5" />
          <th className="px-6 py-2.5 text-left text-[12px] font-OpenSansSemiBold text-subTitleColor">Phone</th>
          <th className="px-6 py-2.5 text-left text-[12px] font-OpenSansSemiBold text-subTitleColor">Status</th>
          <th className="px-6 py-2.5" />
        </tr>
      </thead>
      <tbody>
        {locations.map((loc) => (
          <tr
            key={loc.id}
            onClick={() => onRowClick(loc)}
            className="border-b border-borderColor last:border-b-0 cursor-pointer hover:bg-backgroundColor"
          >
            <td className="pl-[72px] pr-6 py-3 text-[13px] font-OpenSansSemiBold text-titleColor">
              <Highlight text={loc.companyName} keyword={keyword} />
            </td>
            <td className="px-6 py-3 text-[13px] font-OpenSansRegular text-subTitleColor">{loc.branch?.name || "—"}</td>
            <td className="px-6 py-3 text-[13px] font-OpenSansRegular text-subTitleColor">
              {loc.contact ? <Highlight text={loc.contact} keyword={keyword} /> : "—"}
            </td>
            <td className="px-6 py-3" />
            <td className="px-6 py-3 text-[13px] font-OpenSansRegular text-subTitleColor">
              {loc.phone ? <Highlight text={loc.phone} keyword={keyword} /> : "—"}
            </td>
            <td className="px-6 py-3">
              <StatusBadge archived={loc.isArchived} />
            </td>
            <td className="px-6 py-3" onClick={(e) => e.stopPropagation()}>
              {(canEdit || canArchive) && !loc.isArchived && (
                <RowMenu
                  canEdit={canEdit}
                  canArchive={canArchive}
                  onEdit={() => onEdit(loc)}
                  onArchive={() => onArchive(loc)}
                  editLabel="Edit Location"
                  archiveLabel="Archive Location"
                />
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RowMenu({ onEdit, onArchive, canEdit = true, canArchive = true, editLabel = "Edit Customer", archiveLabel = "Archive Customer" }) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const MENU_WIDTH = 190;

  // The dropdown is rendered into a body portal so it escapes the
  // overflow-hidden ancestors (the table card and the expand/collapse
  // animation wrappers) that would otherwise clip it. Because it is
  // position:fixed, we measure the trigger and follow it on scroll/resize.
  useLayoutEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      setCoords({ top: rect.bottom + 4, left: rect.right - MENU_WIDTH });
    };
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open]);

  return (
    <div className="flex justify-center">
      <button ref={buttonRef} onClick={() => setOpen((o) => !o)} className="text-subTitleColor">
        <FiMoreVertical size={18} />
      </button>
      {open &&
        createPortal(
          <OutsideClickHandler onOutsideClick={() => setOpen(false)}>
            <div
              style={{ position: "fixed", top: coords.top, left: coords.left, width: MENU_WIDTH }}
              className="bg-white border border-borderColor rounded-[8px] shadow-lg z-[100] px-1 py-1 flex flex-col gap-[2px]"
            >
            {canEdit && (
              <button
                onClick={() => {
                  onEdit();
                  setOpen(false);
                }}
                className="w-full flex items-center gap-3 min-h-[36px] px-3 py-[6px] rounded-[8px] text-left text-[14px] font-InterMedium leading-[20px] text-[#1A2332] hover:bg-backgroundColor"
              >
                <span className="flex items-start pt-[3px]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M14 12.8332C14.2761 12.8332 14.5 13.057 14.5 13.3332C14.5 13.6093 14.2761 13.8332 14 13.8332H7.99999C7.72385 13.8332 7.49999 13.6093 7.49999 13.3332C7.49999 13.057 7.72385 12.8332 7.99999 12.8332H14Z"
                      fill="#7C829A"
                    />
                    <path
                      d="M12.8333 3.41536C12.8333 3.1727 12.7367 2.93985 12.5651 2.76823C12.3935 2.5966 12.1607 2.5 11.918 2.5C11.6753 2.5 11.4425 2.59661 11.2708 2.76823L3.26367 10.7754C3.16461 10.8744 3.092 10.9971 3.05274 11.1315L2.57878 12.7539L4.20183 12.2806C4.33633 12.2414 4.45952 12.1687 4.5586 12.0697L12.5651 4.0625C12.7367 3.89092 12.8333 3.658 12.8333 3.41536ZM13.8333 3.41536C13.8333 3.92322 13.6313 4.41042 13.2721 4.76953L5.26563 12.7767C5.04757 12.9947 4.77785 13.154 4.48177 13.2402L2.56706 13.7988C2.42374 13.8406 2.27156 13.8437 2.12696 13.8066C1.98234 13.7696 1.85038 13.6941 1.74479 13.5885C1.63916 13.4829 1.56377 13.3504 1.52669 13.2057C1.48968 13.0611 1.49205 12.909 1.53386 12.7656L2.09245 10.8509C2.17882 10.5552 2.33873 10.2862 2.55664 10.0684L10.5638 2.0612C10.923 1.70204 11.41 1.5 11.918 1.5C12.4259 1.5 12.913 1.70204 13.2721 2.0612C13.6312 2.42035 13.8333 2.90748 13.8333 3.41536Z"
                      fill="#7C829A"
                    />
                    <path
                      d="M9.64647 2.97964C9.84173 2.78438 10.1582 2.78438 10.3535 2.97964L12.3535 4.97964C12.5488 5.1749 12.5488 5.49141 12.3535 5.68667C12.1582 5.88194 11.8417 5.88194 11.6465 5.68667L9.64647 3.68667C9.45121 3.49141 9.45121 3.1749 9.64647 2.97964Z"
                      fill="#7C829A"
                    />
                  </svg>
                </span>
                {editLabel}
              </button>
            )}
            {canArchive && (
              <button
                onClick={() => {
                  onArchive();
                  setOpen(false);
                }}
                className="w-full flex items-center gap-3 min-h-[36px] px-3 py-[6px] rounded-[8px] text-left text-[14px] font-InterMedium leading-[20px] text-[#EF4444] hover:bg-backgroundColor"
              >
                <span className="flex items-start pt-[3px]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M14.1666 2.66667C14.1666 2.57462 14.092 2.5 14 2.5H1.99998C1.90793 2.5 1.83331 2.57462 1.83331 2.66667V4.66667C1.83331 4.75871 1.90793 4.83333 1.99998 4.83333H14C14.092 4.83333 14.1666 4.75871 14.1666 4.66667V2.66667ZM15.1666 4.66667C15.1666 5.311 14.6443 5.83333 14 5.83333H1.99998C1.35565 5.83333 0.833313 5.311 0.833313 4.66667V2.66667C0.833313 2.02233 1.35565 1.5 1.99998 1.5H14C14.6443 1.5 15.1666 2.02233 15.1666 2.66667V4.66667Z"
                      fill="#EF4444"
                    />
                    <path
                      d="M2.16665 12.6667V5.33333C2.16665 5.05719 2.3905 4.83333 2.66665 4.83333C2.94279 4.83333 3.16665 5.05719 3.16665 5.33333V12.6667C3.16665 12.8877 3.25451 13.0996 3.41079 13.2559C3.56707 13.4121 3.77897 13.5 3.99998 13.5H12C12.221 13.5 12.4329 13.4121 12.5892 13.2559C12.7455 13.0996 12.8333 12.8877 12.8333 12.6667V5.33333C12.8333 5.05719 13.0572 4.83333 13.3333 4.83333C13.6095 4.83333 13.8333 5.05719 13.8333 5.33333V12.6667C13.8333 13.1529 13.64 13.6191 13.2962 13.9629C12.9524 14.3067 12.4862 14.5 12 14.5H3.99998C3.51375 14.5 3.04757 14.3067 2.70376 13.9629C2.35994 13.6191 2.16665 13.1529 2.16665 12.6667Z"
                      fill="#EF4444"
                    />
                    <path
                      d="M9.33331 7.5C9.60946 7.5 9.83331 7.72386 9.83331 8C9.83331 8.27614 9.60946 8.5 9.33331 8.5H6.66665C6.39051 8.5 6.16665 8.27614 6.16665 8C6.16665 7.72386 6.39051 7.5 6.66665 7.5H9.33331Z"
                      fill="#EF4444"
                    />
                  </svg>
                </span>
                {archiveLabel}
              </button>
            )}
          </div>
          </OutsideClickHandler>,
          document.body
        )}
    </div>
  );
}

function Pagination({ page, totalPages, totalCount, pageSize, currentCount, onChange }) {
  const from = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = (page - 1) * pageSize + currentCount;

  const pages = useMemo(() => {
    const result = [];
    for (let i = 1; i <= totalPages; i += 1) result.push(i);
    if (totalPages <= 7) return result;
    // Compact: 1 2 3 ... last
    const compact = [1, 2, 3];
    if (page > 3 && page < totalPages) compact.push(page);
    compact.push(totalPages);
    return [...new Set(compact)].sort((a, b) => a - b);
  }, [totalPages, page]);

  return (
    <div className="flex items-center justify-between px-6 py-4">
      <div className="flex flex-col justify-center text-[14px] font-OpenSansRegular leading-[20px] break-words">
        <span>
          <span className="text-[#868DA6]">Showing </span>
          <span className="text-[#1A2332] font-OpenSansBold">{from}</span>
          <span className="text-[#868DA6]"> to </span>
          <span className="text-[#1A2332] font-OpenSansBold">{to} </span>
          <span className="text-[#868DA6]">of </span>
          <span className="text-[#1A2332] font-OpenSansBold">{totalCount}</span>
          <span className="text-[#868DA6]"> results</span>
        </span>
      </div>
      <div className="flex items-center gap-1">
        <button
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
          className="h-[34px] px-3 rounded-[6px] border border-borderColor text-[13px] font-OpenSansRegular text-titleColor disabled:text-inputFieldTextColor disabled:cursor-not-allowed"
        >
          Previous
        </button>
        {pages.map((p, i) => {
          const prev = pages[i - 1];
          const gap = prev && p - prev > 1;
          return (
            <Fragment key={p}>
              {gap && <span className="px-1 text-subTitleColor">…</span>}
              <button
                onClick={() => onChange(p)}
                className={`h-[34px] min-w-[34px] px-2 rounded-[6px] text-[13px] font-OpenSansRegular ${
                  p === page
                    ? "bg-lightBlue text-blueColor font-OpenSansSemiBold"
                    : "text-titleColor hover:bg-backgroundColor"
                }`}
              >
                {p}
              </button>
            </Fragment>
          );
        })}
        <button
          disabled={page >= totalPages}
          onClick={() => onChange(page + 1)}
          className="h-[34px] px-3 rounded-[6px] border border-borderColor text-[13px] font-OpenSansRegular text-titleColor disabled:text-inputFieldTextColor disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  );
}

function ArchivedTab({ accounts, onRestore, canRestore = false }) {
  return (
    <div className="px-6 py-4">
      <div className="bg-white rounded-[12px] overflow-hidden shadow-[0px_1px_2px_rgba(0,0,0,0.06)] outline outline-1 -outline-offset-1 outline-[#E5E8F3]">
        <table className="w-full">
          <thead>
            <tr className="bg-[#F8F9FE] border-b border-[#E5E8F3]">
              <th className="h-[44px] px-6 py-3 text-left text-[12px] font-OpenSansSemiBold leading-[18px] text-[#1A2332]">
                Customer
              </th>
              <th className="h-[44px] px-6 py-3 text-left text-[12px] font-OpenSansSemiBold leading-[18px] text-[#1A2332]">
                Locations
              </th>
              <th className="h-[44px] px-6 py-3 text-left text-[12px] font-OpenSansSemiBold leading-[18px] text-[#1A2332]">
                Archived By
              </th>
              <th className="h-[44px] px-6 py-3 text-left text-[12px] font-OpenSansSemiBold leading-[18px] text-[#1A2332]">
                Archived Date
              </th>
              <th className="h-[44px] px-6 py-3 bg-[#F8F9FE]" />
            </tr>
          </thead>
          <tbody>
            {accounts.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-[14px] text-subTitleColor font-OpenSansRegular">
                  No archived customers.
                </td>
              </tr>
            ) : (
              accounts.map((account) => {
                const locationCount = (account.customers || []).length;
                return (
                  <tr key={account.id} className="border-b border-[#E5E8F3] last:border-b-0 hover:bg-[#F8F9FE]">
                    <td className="h-[54px] px-6 py-4 text-[14px] font-OpenSansSemiBold leading-[20px] text-[#1A2332]">
                      {account.name}
                    </td>
                    <td className="h-[54px] px-6 py-4 text-[14px] font-OpenSansRegular leading-[20px] text-[#1A2332]">
                      {locationCount} Location{locationCount === 1 ? "" : "s"}
                    </td>
                    <td className="h-[54px] px-6 py-4 text-[14px] font-OpenSansRegular leading-[20px] text-[#1A2332]">
                      {account.archivedBy?.name || "—"}
                    </td>
                    <td className="h-[54px] px-6 py-4 text-[14px] font-OpenSansRegular leading-[20px] text-[#1A2332]">
                      {account.archivedAt ? formatDate(account.archivedAt, "MMM D, YYYY") : "—"}
                    </td>
                    <td className="h-[54px] px-6 py-4 text-right">
                      {canRestore && (
                        <button
                          onClick={() => onRestore(account)}
                          className="h-[32px] px-1 rounded-[6px] text-[14px] font-OpenSansSemiBold leading-[20px] tracking-[0.14px] capitalize text-[#0065FD]"
                        >
                          Restore
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Customer;
